/**
 * Milestone engine context for BUDOVA „Objekty k připojení“ / WF table.
 * Same inputs as GET /branch-status → `isMilestoneComplete(..., 'ODBOCENO')`.
 */

import {
  inferBudovaBuildingType,
  isBudovaBranchOriginKind,
  isBudovaBuildingType,
  isMilestoneComplete,
  parseBudovaMilestonesFromMetadata,
  readBudovaBranchOriginKind,
  type BudovaBranchOriginKind,
  type BudovaMilestoneEngineContext,
} from '../constants/object-workflow';
import { isBudovaZrusenaPhase } from './budova-pripojeni-exclusion.util';

export interface BudovaPripojeniListBranchFields {
  status?: string | null;
  metadata?: unknown;
  /** Column / explicit type — same as list DTO `budovaBuildingType`. */
  buildingType?: string | null;
  /** Same key as GET /branch-status — live engine result. */
  odbocenoComplete?: boolean | null;
  /** Live VYKOP statuses of `branchVykopSegmentIds` (deleted omitted). */
  vykopSegmentStatuses?: ReadonlyArray<string | null | undefined> | null;
  junctionSpojkovisteStatus?: string | null;
  branchOriginKind?: string | null;
  /** Junction napojené MT (empty = nespecifikováno). */
  napojeneMtBarvy?: readonly string[] | null;
}

/** Assemble the map / branch-status engine context from list DTO fields. */
export function buildBudovaPripojeniMilestoneEngineContext(
  input: BudovaPripojeniListBranchFields,
): BudovaMilestoneEngineContext {
  const metadata =
    input.metadata && typeof input.metadata === 'object' && !Array.isArray(input.metadata)
      ? (input.metadata as Record<string, unknown>)
      : null;
  const milestones = parseBudovaMilestonesFromMetadata(metadata);
  if (input.odbocenoComplete === true) {
    milestones.odboceno = true;
  }
  const rawOrigin = (input.branchOriginKind ?? '').trim();
  const branchOriginKind: BudovaBranchOriginKind | undefined = isBudovaBranchOriginKind(rawOrigin)
    ? rawOrigin
    : metadata
      ? readBudovaBranchOriginKind(metadata)
      : undefined;
  return {
    milestones,
    currentStatus: input.status ?? null,
    branchVykopStatuses: input.vykopSegmentStatuses ?? undefined,
    junctionSpojkovisteStatus: input.junctionSpojkovisteStatus ?? null,
    branchOriginKind,
    junctionNapojeneMtBarvy: input.napojeneMtBarvy ?? undefined,
  };
}

function readMetadataRecord(metadata: unknown): Record<string, unknown> | null {
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? (metadata as Record<string, unknown>)
    : null;
}

/**
 * Same truth as GET /branch-status `odbocenoComplete` / map stripe.
 * Cancelled / Nerealizované (ZAMITNUTA) buildings are excluded (KPI + table denominator).
 */
export function isBudovaPripojeniOdbocenoComplete(
  input: BudovaPripojeniListBranchFields,
): boolean {
  if (isBudovaZrusenaPhase(input.status)) return false;
  const metadata = readMetadataRecord(input.metadata);
  const explicitType = isBudovaBuildingType(input.buildingType) ? input.buildingType : null;
  const buildingType = inferBudovaBuildingType({
    metadata,
    status: input.status,
    explicitType,
  });
  if (buildingType === 'ZAMITNUTA') return false;
  const ctx = buildBudovaPripojeniMilestoneEngineContext(input);
  if (buildingType) return isMilestoneComplete(buildingType, 'ODBOCENO', ctx);
  return ctx.milestones?.odboceno === true;
}

/** Count rows that the pripojeni table / Odbočka KPI treat as Odbočeno. */
export function countBudovaPripojeniOdbocenoComplete(
  rows: readonly BudovaPripojeniListBranchFields[],
): number {
  return rows.reduce((n, row) => n + (isBudovaPripojeniOdbocenoComplete(row) ? 1 : 0), 0);
}
